import { UserManager, WebStorageStateStore, User } from "oidc-client";
import { ApplicationPaths, ApplicationName } from "./ApiAuthorizationConstants";
import { IResultState } from "./Login";
import getUrl from "../../functions/getUrl";

export interface ISignInState {
  returnUrl: string;
}

export interface IProfile {
  display_name: string;
  companyId: string;
  contactId: string;
  role: Array<string>;
}

export interface IUser {
  profile: IProfile;
}

export interface ICallback {
  callback: Function;
  subscription?: number;
}

export enum AuthenticationResultStatus {
  Redirect = "redirect",
  Success = "success",
  Fail = "fail",
}

export interface ISubscription {
  found: boolean;
  index?: number;
}

export class AuthorizeService {
  _callbacks: Array<ICallback>;
  _nextSubscriptionId: number;
  _profile?: IProfile;
  _isAuthenticated: boolean;
  _userManager?: UserManager;

  // by default pop ups are disabled because they don't work properly on Edge.
  // ff you want to enable pop up authentication simply set this flag to false.
  _popUpDisabled = true;

  constructor() {
    this._callbacks = [];
    this._nextSubscriptionId = 0;
    this._isAuthenticated = false;
  }

  userManager(): UserManager {
    if (!this._userManager) {
      throw new Error("user manager was undefined");
    }
    return this._userManager;
  }

  async isAuthenticated() {
    const user = await this.getUser();
    return !!user;
  }

  async getUser(): Promise<IProfile | null> {
    if (this._profile) {
      return this._profile;
    }

    await this.ensureUserManagerInitialized();
    const user = await this.userManager().getUser();
    if (user && user.profile) {
      console.log("EXPIRES AT", new Date(user.expires_at * 1000).toString());
      return {
        display_name: user.profile["display_name"],
        companyId: user.profile["companyId"],
        contactId: user.profile["contactId"],
        role: user.profile["role"],
      };
    }
    return null;
  }

  transformUserProfile(user: User): IProfile | undefined {
    if (user && user.profile) {
      console.log("EXPIRES AT", new Date(user.expires_at * 1000).toString());
      return {
        display_name: user.profile["display_name"],
        companyId: user.profile["companyId"],
        contactId: user.profile["contactId"],
        role: user.profile["role"],
      };
    }
  }

  async getAccessToken() {
    await this.ensureUserManagerInitialized();
    const user = await this.userManager().getUser();
    return user && user.access_token;
  }

  // we try to authenticate the user in three different ways:
  // 1) We try to see if we can authenticate the user silently. This happens
  //    when the user is already logged in on the IdP and is done using a hidden iframe
  //    on the client.
  // 2) We try to authenticate the user using a PopUp Window. This might fail if there is a
  //    pop-Up blocker or the user has disabled PopUps.
  // 3) If the two methods above fail, we redirect the browser to the IdP to perform a traditional
  //    redirect flow.
  async signIn(state: ISignInState) {
    await this.ensureUserManagerInitialized();
    try {
      const silentUser = await this.userManager().signinSilent(this.createArguments());
      this.updateState(this.transformUserProfile(silentUser));
      return this.success(state);
    } catch (silentError) {
      // user might not be authenticated, fallback to popup authentication
      console.log("Silent authentication error: ", silentError);

      try {
        if (this._popUpDisabled) {
          throw new Error("Popup disabled. Change 'AuthorizeService.js:AuthorizeService._popupDisabled' to false to enable it.");
        }

        const popUpUser = await this.userManager().signinPopup(this.createArguments());
        this.updateState(this.transformUserProfile(popUpUser));
        return this.success(state);
      } catch (popUpError) {
        if (popUpError.message === "Popup window closed") {
          // the user explicitly cancelled the login action by closing an opened popup.
          return this.error("The user closed the window.");
        } else if (!this._popUpDisabled) {
          console.log("Popup authentication error: ", popUpError);
        }

        // popUps might be blocked by the user, fallback to redirect
        try {
          await this.userManager().signinRedirect(this.createArguments(state));
          return this.redirect();
        } catch (redirectError) {
          console.log("Redirect authentication error: ", redirectError);
          return this.error(redirectError);
        }
      }
    }
  }

  async completeSignIn(url: string): Promise<IResultState> {
    try {
      await this.ensureUserManagerInitialized();
      const user = await this.userManager().signinCallback(url);

      this.updateState(this.transformUserProfile(user));
      return this.success(user && user.state);
    } catch (error) {
      console.log("There was an error signing in: ", error);
      return this.error("There was an error signing in.");
    }
  }

  // we try to sign out the user in two different ways:
  // 1) We try to do a sign-out using a PopUp Window. This might fail if there is a
  //    pop-Up blocker or the user has disabled PopUps.
  // 2) If the method above fails, we redirect the browser to the IdP to perform a traditional
  //    post logout redirect flow.
  async signOut(state: any) {
    await this.ensureUserManagerInitialized();
    try {
      if (this._popUpDisabled) {
        throw new Error("Popup disabled. Change 'AuthorizeService.js:AuthorizeService._popupDisabled' to false to enable it.");
      }

      await this.userManager().signoutPopup(this.createArguments());
      this.updateState(undefined);
      return this.success(state);
    } catch (popupSignOutError) {
      console.log("Popup signout error: ", popupSignOutError);
      try {
        await this.userManager().signoutRedirect(this.createArguments(state));
        return this.redirect();
      } catch (redirectSignOutError) {
        console.log("Redirect signout error: ", redirectSignOutError);
        return this.error(redirectSignOutError);
      }
    }
  }

  async completeSignOut(url: string) {
    await this.ensureUserManagerInitialized();
    try {
      const response = await this.userManager().signoutCallback(url);
      this.updateState(undefined);
      return this.success(response);
    } catch (error) {
      console.log(`There was an error trying to log out '${error}'.`);
      return this.error(error);
    }
  }

  updateState(profile?: IProfile) {
    this._profile = profile;
    this._isAuthenticated = !!this._profile;
    this.notifySubscribers();
  }

  subscribe(callback: () => void) {
    this._callbacks.push({
      callback,
      subscription: this._nextSubscriptionId++,
    });
    return this._nextSubscriptionId - 1;
  }

  unsubscribe(subscriptionId: number) {
    const subscriptionIndex: Array<ISubscription> = this._callbacks
      .map((element, index) => (element.subscription === subscriptionId ? { found: true, index } : { found: false }))
      .filter(element => element.found === true);
    if (subscriptionIndex.length !== 1) {
      throw new Error(`Found an invalid number of subscriptions ${subscriptionIndex.length}`);
    }

    if (subscriptionIndex[0].index !== undefined) {
      this._callbacks = this._callbacks.splice(subscriptionIndex[0].index, 1);
    }
  }

  notifySubscribers() {
    for (let i = 0; i < this._callbacks.length; i++) {
      const callback = this._callbacks[i].callback;
      callback();
    }
  }

  createArguments(state?: any) {
    return { useReplaceToNavigate: true, data: state };
  }

  error(message: string): IResultState {
    return { status: AuthenticationResultStatus.Fail, message };
  }

  success(state: any): IResultState {
    return { status: AuthenticationResultStatus.Success, state };
  }

  redirect(): IResultState {
    return { status: AuthenticationResultStatus.Redirect };
  }

  async ensureUserManagerInitialized() {
    if (this._userManager !== undefined) {
      return;
    }

    let response = await fetch(getUrl(ApplicationPaths.ApiAuthorizationClientConfigurationUrl));
    if (!response.ok) {
      throw new Error(`Could not load settings for '${ApplicationName}'`);
    }

    let settings = await response.json();
    settings.automaticSilentRenew = true;
    settings.includeIdTokenInSilentRenew = true;
    settings.userStore = new WebStorageStateStore({
      prefix: ApplicationName,
    });

    this._userManager = new UserManager(settings);

    this.userManager().events.addUserSignedOut(async () => {
      await this.userManager().removeUser();
      this.updateState(undefined);
      console.log("AUTHSERVICE_EVENT: User signed out", new Date().toString());
    });
  }

  static get instance() {
    // tslint:disable-next-line: no-use-before-declare
    return authService;
  }
}

const authService = new AuthorizeService();

export default authService;
